import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { sendRequest } from '../ApiRequest';
import { BASE_URL } from '../URL/constants';
import getCurrentUser from './common/GetCurrentUser';
import MessageModal from './Common/MessageModal';
import useMessageModal from '../hooks/useMessageModal';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaArrowLeft } from 'react-icons/fa';
import OrderItemsSummary from './common/OrderItemsSummary';
import './CustomerInfoForm.css';

const CustomerInfoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = getCurrentUser();
  const reduxCartItems = useSelector((state) => state.addItem);
  const { modalState, showSuccess, showError, closeModal } = useMessageModal();
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    shippingAddressLine1: '',
    shippingAddressLine2: '',
    shippingCity: '',
    shippingState: '',
    shippingCountry: 'India',
    shippingPincode: '',
    shippingZip: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingCity: '',
    billingState: '',
    billingCountry: 'India',
    billingPincode: '',
    billingZip: '',
    useSameAddress: true
  });
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingAddressIds, setExistingAddressIds] = useState({
    shipping: null,
    billing: null
  });

  useEffect(() => {
    // Only fetch customer data if user is logged in and data is available
    if (currentUser && currentUser.data && currentUser.data.customer) {
      fetchCustomerData();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch cart items - only selected items for checkout
  useEffect(() => {
    const fetchCartData = async () => {
      setCartLoading(true);
      try {
        let finalCartItems = [];
        let allCartItems = [];
        
        // For logged-in users, use API cart
        if (currentUser?.data?.customer) {
          try {
            // Add a delay to ensure cart is synced after login
            // Wait longer if we just logged in (check if cart-updated event was recent)
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            const response = await sendRequest(
              'GET',
              `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart`,
              null,
              { Authorization: currentUser.data.token }
            );
            const apiCartItems = response.data.cart_items || [];
            
            // Fetch discount information for each cart item
            const itemsWithDiscount = await Promise.all(
              apiCartItems.map(async (cartItem) => {
                try {
                  const itemResponse = await sendRequest(
                    'GET',
                    `${BASE_URL}/v1/public_site/menus/item_detail?id=${cartItem.item_id}`,
                    null,
                    { Authorization: currentUser.data.token }
                  );
                  const itemDetails = itemResponse.data.data;
                  const discount = itemDetails.sales_discount || 0;
                  return {
                    ...cartItem,
                    sales_discount: discount,
                    item_name: itemDetails.item_name,
                    item_code: itemDetails.item_code,
                    item_image_url: itemDetails.item_image_url,
                    unit_name: itemDetails.unit_name
                  };
                } catch (error) {
                  return {
                    ...cartItem,
                    sales_discount: 0
                  };
                }
              })
            );
            allCartItems = itemsWithDiscount;
          } catch (apiError) {
            console.error('Error fetching cart from API:', apiError);
            allCartItems = reduxCartItems;
          }
        } else {
          // If not logged in, get from localStorage
          const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
          const guestCartItems = Array.isArray(guestCart) ? guestCart : Object.values(guestCart);
          const uniqueItems = [];
          const seenItemIds = new Set();
          
          guestCartItems.forEach(item => {
            const itemId = item.item_id || item.id;
            if (itemId && !seenItemIds.has(itemId)) {
              seenItemIds.add(itemId);
              uniqueItems.push(item);
            }
          });
          
          reduxCartItems.forEach(item => {
            const itemId = item.item_id || item.id;
            if (itemId && !seenItemIds.has(itemId)) {
              seenItemIds.add(itemId);
              uniqueItems.push(item);
            }
          });
          
          allCartItems = uniqueItems;
        }
        
        // Get selected items from localStorage (saved by CheckoutHandler)
        const selectedItemIdsStr = localStorage.getItem('checkoutSelectedItems');
        if (selectedItemIdsStr) {
          try {
            const selectedItemIds = JSON.parse(selectedItemIdsStr);
            console.log('CustomerInfoForm - Selected item IDs from localStorage:', selectedItemIds);
            console.log('CustomerInfoForm - All cart items:', allCartItems.map(i => ({ id: i.id, item_id: i.item_id })));
            
            // Filter to only selected items
            // selectedItemIds could be either cart_item IDs (item.id) or product item IDs (item.item_id)
            finalCartItems = allCartItems.filter(item => {
              const cartItemId = item.id; // cart_item ID
              const productItemId = item.item_id; // product item ID
              // Check if either cart_item ID or product item ID matches
              return selectedItemIds.some(selectedId => {
                const selectedIdNum = parseInt(selectedId);
                return selectedIdNum === cartItemId || selectedIdNum === productItemId || 
                       String(selectedId) === String(cartItemId) || String(selectedId) === String(productItemId);
              });
            });
            
            console.log('CustomerInfoForm - Filtered selected items:', finalCartItems.length);
          } catch (error) {
            console.error('Error parsing selected items:', error);
            // If error parsing, use all items
            finalCartItems = allCartItems;
          }
        } else {
          // If no selected items saved, use all items (fallback for backward compatibility)
          console.log('CustomerInfoForm - No selected items in localStorage, using all items');
          finalCartItems = allCartItems;
        }
        
        setCartItems(finalCartItems);
      } catch (error) {
        console.error('Error in fetchCartData:', error);
        setCartItems(reduxCartItems);
      } finally {
        setCartLoading(false);
      }
    };

    fetchCartData();
    
    // Listen to cart-updated events to refresh cart when items are synced
    const handleCartUpdated = () => {
      if (currentUser?.data?.customer) {
        // Wait a bit before fetching to ensure sync is complete
        setTimeout(() => {
          fetchCartData();
        }, 500);
      }
    };
    
    // Also listen to user-logged-in event to refresh cart after login
    const handleUserLoggedIn = () => {
      // Wait longer after login to ensure cart sync completes
      setTimeout(() => {
        if (currentUser?.data?.customer) {
          fetchCartData();
        }
      }, 1500);
    };
    
    window.addEventListener('cart-updated', handleCartUpdated);
    window.addEventListener('user-logged-in', handleUserLoggedIn);
    
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdated);
      window.removeEventListener('user-logged-in', handleUserLoggedIn);
    };
  }, [currentUser, reduxCartItems]);

  const fetchCustomerData = async () => {
    try {
      // Check if user is logged in and has required data
      if (!currentUser || !currentUser.data || !currentUser.data.customer || !currentUser.data.token) {
        showError('Please login to continue');
        navigate('/login');
        return;
      }

      const response = await sendRequest(
        'GET',
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}`,
        null,
        { Authorization: currentUser.data.token }
      );
      
      const customer = response.data;
      const addresses = customer.addresses || [];
      
      // Find shipping and billing addresses
      const shippingAddress = addresses.find(addr => addr.address_type === 'shipping') || {};
      const billingAddress = addresses.find(addr => addr.address_type === 'billing') || {};
      
      // Store existing address IDs for update
      setExistingAddressIds({
        shipping: shippingAddress.id || null,
        billing: billingAddress.id || null
      });
      
      setFormData({
        firstName: customer.first_name || '',
        lastName: customer.last_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        shippingAddressLine1: shippingAddress.address_line_1 || '',
        shippingAddressLine2: shippingAddress.address_line_2 || '',
        shippingCity: shippingAddress.city || '',
        shippingState: shippingAddress.state || '',
        shippingCountry: shippingAddress.country || 'India',
        shippingPincode: shippingAddress.postal_code || '',
        shippingZip: shippingAddress.zip || '',
        billingAddressLine1: billingAddress.address_line_1 || '',
        billingAddressLine2: billingAddress.address_line_2 || '',
        billingCity: billingAddress.city || '',
        billingState: billingAddress.state || '',
        billingCountry: billingAddress.country || 'India',
        billingPincode: billingAddress.postal_code || '',
        billingZip: billingAddress.zip || '',
        useSameAddress: true
      });
    } catch (error) {
      console.error('Error fetching customer data:', error);
      console.error('Error details:', error.response?.data);
      showError(`Failed to load customer information: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUseSameAddress = (checked) => {
    setFormData(prev => ({
      ...prev,
      useSameAddress: checked,
      billingAddressLine1: checked ? prev.shippingAddressLine1 : prev.billingAddressLine1,
      billingAddressLine2: checked ? prev.shippingAddressLine2 : prev.billingAddressLine2,
      billingCity: checked ? prev.shippingCity : prev.billingCity,
      billingState: checked ? prev.shippingState : prev.billingState,
      billingCountry: checked ? prev.shippingCountry : prev.billingCountry,
      billingPincode: checked ? prev.shippingPincode : prev.billingPincode,
      billingZip: checked ? prev.shippingZip : prev.billingZip,
    }));
  };

  const validateForm = () => {
    if (!formData.phone.trim()) {
      showError('Phone number is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Update customer data
      await sendRequest(
        'PUT',
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}`,
        {
          customer: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            addresses_attributes: [
              {
                ...(existingAddressIds.shipping && { id: existingAddressIds.shipping }),
                address_type: 'shipping',
                address_line_1: formData.shippingAddressLine1,
                address_line_2: formData.shippingAddressLine2,
                city: formData.shippingCity,
                state: formData.shippingState,
                country: formData.shippingCountry,
                postal_code: formData.shippingPincode,
              },
              {
                ...(existingAddressIds.billing && { id: existingAddressIds.billing }),
                address_type: 'billing',
                address_line_1: formData.useSameAddress ? formData.shippingAddressLine1 : formData.billingAddressLine1,
                address_line_2: formData.useSameAddress ? formData.shippingAddressLine2 : formData.billingAddressLine2,
                city: formData.useSameAddress ? formData.shippingCity : formData.billingCity,
                state: formData.useSameAddress ? formData.shippingState : formData.billingState,
                country: formData.useSameAddress ? formData.shippingCountry : formData.billingCountry,
                postal_code: formData.useSameAddress ? formData.shippingPincode : formData.billingPincode,
              },
            ],
          },
        },
        { Authorization: currentUser.data.token }
      );

      showSuccess('Customer information updated successfully!');
      
      // Navigate to payment page with form data including address IDs
      setTimeout(() => {
        navigate('/payment', {
          state: {
            formData: {
              ...formData,
              shippingAddressId: existingAddressIds.shipping,
              billingAddressId: existingAddressIds.billing,
              billingAddressLine1: formData.useSameAddress ? formData.shippingAddressLine1 : formData.billingAddressLine1,
              billingAddressLine2: formData.useSameAddress ? formData.shippingAddressLine2 : formData.billingAddressLine2,
              billingCity: formData.useSameAddress ? formData.shippingCity : formData.billingCity,
              billingState: formData.useSameAddress ? formData.shippingState : formData.billingState,
              billingCountry: formData.useSameAddress ? formData.shippingCountry : formData.billingCountry,
              billingPincode: formData.useSameAddress ? formData.shippingPincode : formData.billingPincode,
              billingZip: formData.useSameAddress ? formData.shippingZip : formData.billingZip,
            }
          }
        });
      }, 1500);

    } catch (error) {
      console.error('Error updating customer data:', error);
      showError('Failed to update customer information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="customer-form-loading">
        <div className="spinner"></div>
        <p>Loading your information...</p>
      </div>
    );
  }

  return (
    <div className="customer-form-wrapper">
      <div className="customer-form-container">
        {!cartLoading && cartItems.length > 0 && (
          <OrderItemsSummary cartItems={cartItems} />
        )}
        <div className="customer-form-header">
          <h1>Customer Information</h1>
          <p>Please review and update your information before checkout</p>
        </div>

        <form onSubmit={handleSubmit} className="customer-form">
          {/* Personal Information Section */}
          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <FaUser />
              </div>
              <h3>Personal Information</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number <span className="required">*</span></label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  readOnly
                  className="readonly-field"
                  title="Phone number cannot be changed"
                  required
                />
              </div>
            </div>
          </div>

          {/* Shipping Address Section */}
          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <FaMapMarkerAlt />
              </div>
              <h3>Shipping Address</h3>
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="shippingAddressLine1">Address Line 1</label>
                <input
                  type="text"
                  id="shippingAddressLine1"
                  name="shippingAddressLine1"
                  value={formData.shippingAddressLine1}
                  onChange={handleInputChange}
                  placeholder="Street address, P.O. box"
                />
              </div>
              <div className="form-group full-width">
                <label htmlFor="shippingAddressLine2">Address Line 2</label>
                <input
                  type="text"
                  id="shippingAddressLine2"
                  name="shippingAddressLine2"
                  value={formData.shippingAddressLine2}
                  onChange={handleInputChange}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </div>
              <div className="form-group">
                <label htmlFor="shippingCity">City</label>
                <input
                  type="text"
                  id="shippingCity"
                  name="shippingCity"
                  value={formData.shippingCity}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                />
              </div>
              <div className="form-group">
                <label htmlFor="shippingState">State</label>
                <input
                  type="text"
                  id="shippingState"
                  name="shippingState"
                  value={formData.shippingState}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                />
              </div>
              <div className="form-group">
                <label htmlFor="shippingPincode">Pincode</label>
                <input
                  type="text"
                  id="shippingPincode"
                  name="shippingPincode"
                  value={formData.shippingPincode}
                  onChange={handleInputChange}
                  placeholder="Enter pincode"
                />
              </div>
              <div className="form-group">
                <label htmlFor="shippingCountry">Country</label>
                <input
                  type="text"
                  id="shippingCountry"
                  name="shippingCountry"
                  value={formData.shippingCountry}
                  onChange={handleInputChange}
                  placeholder="Enter country"
                />
              </div>
            </div>
          </div>

          {/* Billing Address Section */}
          <div className="form-section">
            <div className="same-address-toggle">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="useSameAddress"
                  checked={formData.useSameAddress}
                  onChange={(e) => handleUseSameAddress(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span className="checkbox-text">Use same address for billing</span>
              </label>
            </div>

            {!formData.useSameAddress && (
              <div className="billing-address">
                <div className="section-header">
                  <div className="section-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <h3>Billing Address</h3>
                </div>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label htmlFor="billingAddressLine1">Address Line 1</label>
                    <input
                      type="text"
                      id="billingAddressLine1"
                      name="billingAddressLine1"
                      value={formData.billingAddressLine1}
                      onChange={handleInputChange}
                      placeholder="Street address, P.O. box"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="billingAddressLine2">Address Line 2</label>
                    <input
                      type="text"
                      id="billingAddressLine2"
                      name="billingAddressLine2"
                      value={formData.billingAddressLine2}
                      onChange={handleInputChange}
                      placeholder="Apartment, suite, unit, building, floor, etc."
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="billingCity">City</label>
                    <input
                      type="text"
                      id="billingCity"
                      name="billingCity"
                      value={formData.billingCity}
                      onChange={handleInputChange}
                      placeholder="Enter city"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="billingState">State</label>
                    <input
                      type="text"
                      id="billingState"
                      name="billingState"
                      value={formData.billingState}
                      onChange={handleInputChange}
                      placeholder="Enter state"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="billingPincode">Pincode</label>
                    <input
                      type="text"
                      id="billingPincode"
                      name="billingPincode"
                      value={formData.billingPincode}
                      onChange={handleInputChange}
                      placeholder="Enter pincode"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="billingCountry">Country</label>
                    <input
                      type="text"
                      id="billingCountry"
                      name="billingCountry"
                      value={formData.billingCountry}
                      onChange={handleInputChange}
                      placeholder="Enter country"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => {
                // Clear selected items from localStorage when canceling
                localStorage.removeItem('checkoutSelectedItems');
                navigate(-1);
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Continue to Payment'}
            </button>
          </div>
        </form>
      </div>

      <MessageModal
        isOpen={modalState.isOpen}
        message={modalState.message}
        type={modalState.type}
        onClose={closeModal}
        autoClose={modalState.autoClose}
        autoCloseDelay={modalState.autoCloseDelay}
        showCloseButton={modalState.showCloseButton}
      />
    </div>
  );
};

export default CustomerInfoForm; 